"use client";

import { useEffect } from "react";

const RELOAD_KEY = "chunk-reload-at";
const RELOAD_WINDOW_MS = 10_000;
const CHUNK_ERROR = /ChunkLoadError|Loading chunk|Failed to load chunk|Failed to fetch dynamically imported module/;

function isChunkError(value: unknown) {
  if (!value) return false;
  const { name, message } = value as { name?: string; message?: string };
  return CHUNK_ERROR.test(`${name ?? ""} ${message ?? String(value)}`);
}

/**
 * Reloads the page once when a lazy chunk 404s, which happens when a tab or
 * cached HTML outlives a deploy and still points at old hashed asset names.
 * The sessionStorage timestamp prevents a reload loop if the chunk is
 * genuinely broken.
 */
export default function ChunkReload() {
  useEffect(() => {
    function reloadOnce() {
      try {
        const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
        if (Date.now() - last <= RELOAD_WINDOW_MS) return;
        window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      } catch {
        return;
      }
      window.location.reload();
    }

    function onError(event: ErrorEvent) {
      if (isChunkError(event.error ?? event.message)) reloadOnce();
    }

    function onRejection(event: PromiseRejectionEvent) {
      if (isChunkError(event.reason)) reloadOnce();
    }

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
